import { useSession } from '@/lib/auth/session';
import { isAdministrator, type Session } from '@/lib/types';

/**
 * 등록·수정·삭제 버튼을 화면에 띄울지 판단한다.
 *
 * **인가가 아니다.** `RequireAuth` 와 같은 성격의 UX 장치이며, 실제 판정은 백엔드가 매 요청마다 한다.
 * 이 판단을 항상 `true` 로 바꿔도 데이터가 새어서는 안 된다.
 *
 * v1 은 메뉴 키 × 동작 표를 브라우저가 통째로 들고 판정했다(`lib/adminTestClientPermissions.ts`).
 * 이제 그 표는 **백엔드가 세션 응답의 `menuPermissions` 로 내려준다**(서버 권위). 판정은 두 갈래다.
 *  - **관리자**(admin·super_admin)는 전권이다. 관리자에게는 `menuPermissions` 가 비어 오므로
 *    `userType` 으로 판단한다 — 전체 메뉴 키의 우주가 프론트(`menuConfig.ts`)에 있기 때문이다.
 *  - **비관리자**는 서버가 준 `menuPermissions` 에서 해당 메뉴의 동작 허용 여부를 본다. 없으면 거부다.
 *    그 목록에는 **부서 소속 기본 조회**(`DepartmentBaseline`)와 담당 역할의 그랜트가 이미 합쳐져 온다.
 *
 * 판단 로직은 순수 함수로 두고 훅은 그것을 세션에 이어 붙이기만 한다 — 추후 네이티브 앱이 같은 판단을
 * 재사용할 수 있도록(배경: docs/04_2026-07-28_cross-platform-mobile-design.md).
 */

/**
 * 동작 네 가지. 예전에는 `upload` 가 따로 있었지만 2026-08-13 에 `create` 로 합쳤다 —
 * 서른네 메뉴 중 열한 개만 업로드를 판정했고, 등록과 다르게 준 곳은 사실상 없었다(`MenuAction`).
 */
export type MenuActionType = 'view' | 'create' | 'edit' | 'delete';

type MenuActionField = 'canView' | 'canCreate' | 'canEdit' | 'canDelete';

const ACTION_FIELD: Record<MenuActionType, MenuActionField> = {
  view: 'canView',
  create: 'canCreate',
  edit: 'canEdit',
  delete: 'canDelete',
};

/*
  인사자료를 역할로 한 번 더 거르던 문(`lib/auth/hrTeam.ts` · 백엔드 `HrTeamAccess`)은 2026-08-14 에
  없앴다. 켜진 적 없는 스위치 뒤에 있어 아무것도 지키지 않았고, 지금은 **메뉴 키가 그 일을 한다** —
  `hr_records`·`hr_records_payroll`·`hr_records_evaluation` 은 부서 소속 기본 조회에서 빠져 있어
  「인사 담당」·「급여 담당」·「고과 담당」 역할이나 부서 권한으로 따로 받아야 열린다.
*/

/** 이 세션이 `menuKey` 에서 `action` 을 해도 되는가. 관리자는 전권, 비관리자는 서버가 준 표를 본다(없으면 거부). */
export function resolveMenuCapability(
  session: Session | undefined,
  menuKey: string,
  action: MenuActionType,
): boolean {
  if (!session?.authenticated) return false;
  if (isAdministrator(session.user?.userType)) return true;
  const permission = session.menuPermissions.find((p) => p.menuKey === menuKey);
  return permission?.[ACTION_FIELD[action]] ?? false;
}

/**
 * 등록·수정·삭제 중 하나라도 할 수 있는가(= 관리 버튼 묶음을 띄울지).
 *
 * ★ **`menuKey` 는 필수다** (사용자 지적 2026-09-04). 2026-08-13 에 이 함수가 메뉴 키를 받도록
 * 넓어졌을 때, 키를 <b>선택</b>으로 두고 없으면 옛 동작(관리자만 true)으로 물러서게 했다. 호출부를
 * 한 번에 고치지 않아도 되니 안전해 보였지만, 실제로는 <b>고치지 않은 화면이 조용히 관리자 전용으로
 * 굳는</b> 길이었다 — 권한을 준 사람은 줬다고 믿고, 받은 사람에게는 버튼이 없다. 명함(09-02) ·
 * 월간업무(09-03) · 식당안내(09-04)가 전부 같은 한 줄이었다.
 *
 * 이제 키 없는 호출은 컴파일에서 걸린다. 같은 실수가 다시 조용히 남지 않는다.
 */
export function resolveCanManage(session: Session | undefined, menuKey: string): boolean {
  if (!session?.authenticated) return false;
  if (isAdministrator(session.user?.userType)) return true;
  const permission = session.menuPermissions.find((p) => p.menuKey === menuKey);
  if (!permission) return false;
  return permission.canCreate || permission.canEdit || permission.canDelete;
}

export function useCanManage(menuKey: string): boolean {
  const { data: session } = useSession();
  return resolveCanManage(session, menuKey);
}

/** 특정 메뉴·동작 하나에 대한 판단이 필요할 때(예: 올리기만 되는 화면). */
export function useMenuCapability(menuKey: string, action: MenuActionType): boolean {
  const { data: session } = useSession();
  return resolveMenuCapability(session, menuKey, action);
}

/**
 * **이 현장의 행을 쓸 수 있는가** — 서버 `SiteScopeGuard` 와 같은 판정을 화면에서 미리 한다.
 *
 * 계층1(메뉴 권한)과 **독립**이다. 등록 권한이 있어도 남의 현장 행은 서버가 403 `SCOPE_DENIED` 로
 * 막는다. 그 판정을 화면이 모르면 누를 수 없는 버튼이 뜨고, 누른 사람은 자기가 무엇을 잘못했는지
 * 모른 채 오류만 본다 — 그래서 **버튼을 아예 띄우지 않는다.**
 *
 * 서버가 현장 **이름**으로 견주므로 여기도 이름으로 맞춘다(소속 표가 v1 스키마 그대로 이름을 담는다).
 *
 * @param siteName 판정할 현장 이름. null 이면 아직 고르지 않은 것이라 false
 */
export function resolveCanWriteSite(session: Session | undefined, siteName: string | null): boolean {
  if (!session?.authenticated || !siteName) return false;
  if (isAdministrator(session.user?.userType)) return true;
  // 옛 응답에는 이 칸이 없다. 없으면 제한 없음으로 본다 — 자세한 이유는 `Session.siteScopeAll`.
  if (session.siteScopeAll !== false) return true;
  return (session.siteScopeNames ?? []).includes(siteName);
}

export function useCanWriteSite(siteName: string | null): boolean {
  const { data: session } = useSession();
  return resolveCanWriteSite(session, siteName);
}

/**
 * **이 현장에 배치돼 있는가** — 현장 마스터 [수정] 의 문 (사용자 결정 2026-09-30).
 *
 * `resolveCanWriteSite` 와 다르다. 그쪽은 열람 규칙이라 본사 인원을 제한 없음으로 보지만, 현장
 * 정보(계약·공기·상태)를 고치는 손은 본사 직원이라도 **자기가 서 있는 현장**뿐이다. 현장 관리 권한을
 * 받은 사람도 같다. 서버 `SiteScopeGuard.requireAssigned` 가 같은 판정으로 저장을 받는다.
 *
 * 옛 응답에는 `assignedSiteNames` 가 없다 — 그때는 거부다. 버튼이 안 뜰 뿐 인가는 서버가 한다.
 */
export function resolveIsAssignedToSite(session: Session | undefined, siteName: string | null): boolean {
  if (!session?.authenticated || !siteName) return false;
  if (isAdministrator(session.user?.userType)) return true;
  return (session.assignedSiteNames ?? []).includes(siteName);
}

export function useIsAssignedToSite(siteName: string | null): boolean {
  const { data: session } = useSession();
  return resolveIsAssignedToSite(session, siteName);
}

/*
  ★ **주간·월간업무는 위 판정을 지나지 않는다** (사용자 지시 2026-09-03).

  그 화면들은 전 직원이 전 현장에 등록·수정·삭제까지 한다(서버 `DepartmentBaseline.OPEN_TO_EVERYONE`
  · `SiteScopeGuard.requireOpen`). 그래서 메뉴 권한만 보고 버튼을 띄운다 — 여기서 한 번 더 좁히면
  **서버는 허락하는데 누를 자리가 없는** 상태가 된다. 위 두 함수가 좁히는 것은 PMIS·기성처럼
  현장으로 갈리는 자료뿐이다.
*/
